"use client";
import Apple from "@/app/assets/icons/apple";
import GooglePlay from "@/app/assets/icons/googleplay";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import React, { useState, useEffect } from "react";

interface PageHeroSectionProps {
    label1: string;
    label2: string;
    label3: string;
    textCarousel: string[];
    toggleDownloadApp: (arg0: string) => void;
    isLoading: boolean;
}

const PageHeroSection: React.FC<PageHeroSectionProps> = ({
    label1,
    label2,
    label3,
    textCarousel,
    toggleDownloadApp,
    isLoading,
}) => {
    const [positions, setPositions] = useState(textCarousel.map((_, i) => i * 100));

    useEffect(() => {
        const interval = setInterval(() => {
            setPositions((prevPositions) =>
                prevPositions.map((pos) => {
                    const newPos = pos - 100;
                    return newPos < -((textCarousel.length - 1) * 100) ? 100 : newPos;
                }),
            );
        }, 3000);

        return () => clearInterval(interval);
    }, [textCarousel.length]);

    return (
        <div
            id="page-hero-section"
            className="mt-[18px] flex w-full flex-col items-center px-[25px] md:mb-20 md:mt-s15 md:px-0"
        >
            <p
                id="hero-label-1"
                className="mb-4 text-center text-base font-semibold leading-[20.8px] text-neutral-900 md:mb-3 md:text-4xl md:leading-[41.6px] md:tracking-[-0.48px]"
            >
                {label1}
            </p>
            {isLoading ? (
                <div className="mb-4 h-10 w-full animate-pulse rounded bg-gray-300 md:mb-5 md:h-20 md:w-2/4"></div>
            ) : (
                <p
                    id="hero-label-2"
                    className="mb-4 text-center text-4xl font-semibold leading-[41.6px] tracking-[-0.48px] text-neutral-900 md:mb-5 md:text-8xl md:leading-[79.2px] md:tracking-[-1.8px]"
                >
                    {label2}
                </p>
            )}
            <p
                id="hero-label-3"
                className="text-center text-base font-regular leading-[25.6px] text-neutral-900 md:mb-0 md:text-3xl md:leading-[39.84px] md:tracking-[-0.24px]"
            >
                {label3}
            </p>
            <div
                id="hero-text-carousel"
                className="relative mb-0 h-[40px] w-full overflow-hidden md:mb-8"
            >
                {textCarousel.map((text, index) => (
                    <p
                        key={index}
                        id={`hero-carousel-text-${index}`}
                        className="absolute w-full text-center text-base font-semibold leading-[25.6px] text-neutral-900 md:text-3xl md:font-bold md:leading-[39.84px] md:tracking-[-0.24px]"
                        style={{
                            transform: `translateY(${positions[index]}%)`,
                            opacity: positions[index] === 0 ? 1 : 0,
                            transition: "transform 0.5s ease, opacity 0.5s ease",
                        }}
                    >
                        {text}
                    </p>
                ))}
            </div>
            <RegularButton
                id="download-app-button"
                noIcon
                showRightIcon={true}
                rightIcon={
                    <div className="flex h-12 min-w-16 items-center justify-center gap-s4">
                        <Apple className="h-10 w-10 text-white" />
                        <div className="h-full w-[3px] bg-neutralOpacityDark-20"></div>
                        <GooglePlay className="h-10 w-10 text-white" />
                    </div>
                }
                size={ButtonSize.Large}
                variant={ButtonVariant.PrimaryDefault}
                label="Download App"
                className="mb-14 mt-6 w-full max-w-full md:mb-2 md:mt-0 md:max-w-fit"
                onClick={() => toggleDownloadApp("FD_Banner_Download")}
            />
        </div>
    );
};

export default PageHeroSection;
